import { gql } from "lib/apolloClient";
import { studentsAlertFragment } from "modules/CommonFragments";

export const announcementFeedFragment = gql`
  fragment announcementItem on Circular {
    id
    title
    bannerV2 {
      id
      url
      name
      type
      mimeType
      metadata
    }
    description
    isPublished
    isArchived
    publishedAt
    isRead
    isNew
    updatedAt
    archivedAt
    createdBy {
      id
      firstName
      middleName
      lastName
      type
      email
      profileImage
      preferredName
      pronouns
      prefix
      suffix
    }
    publishedBy {
      id
      firstName
      middleName
      lastName
      type
      email
      profileImage
      preferredName
      pronouns
      prefix
      suffix
    }
    updatedBy {
      id
      firstName
      middleName
      lastName
      type
      email
      profileImage
      preferredName
      pronouns
      prefix
      suffix
    }
    archivedBy {
      id
      firstName
      middleName
      lastName
      type
      email
      profileImage
      preferredName
      pronouns
      prefix
      suffix
    }
    state
    isV2
  }
`;

export const announcementDetailsWithRecipients = gql`
  fragment announcementDetailsItem on Circular {
    ...announcementItem
    isSharedWithMe(input: { academicYearId: $academicYearId })
    recipients(input: { academicYearId: $academicYearId })
      @include(if: $isRecipientsVisible) {
      totalCount
      readByCount
    }
  }
  ${announcementFeedFragment}
`;

export const announcementBasicInfo = gql`
  fragment announcementBasicInfoItem on Circular {
    id
    state
    createdBy {
      id
    }
  }
`;

export const selectedAnnouncementsTabCountFragment = gql`
  fragment selectedAnnouncementsTabCountItem on Organization {
    circularFeedV3 {
      totalCount(
        input: {
          createdBy: $createdBy
          states: $states
          createdAfter: $createdAfter
          createdBefore: $createdBefore
          curriculumProgramIds: $curriculumProgramIds
          academicYearId: $academicYearId
          studentIds: $studentIds
          isRead: $isRead
          createdIn: $createdIn
          courseIds: $courseIds
        }
      )
    }
  }
`;

export const unreadAnnouncementsCountFragment = gql`
  fragment unreadAnnouncementsCountItem on CircularConnectionV3 {
    unreadAnnouncementsCount: totalCount(
      input: {
        createdBy: $createdBy
        states: [PUBLISHED]
        createdAfter: $createdAfter
        createdBefore: $createdBefore
        curriculumProgramIds: $curriculumProgramIds
        academicYearId: $academicYearId
        studentIds: $studentIds
        isRead: false
        createdIn: $createdIn
        courseIds: $courseIds
      }
    )
  }
`;

export const userCourseDetailsFragment = gql`
  fragment userCourseDetailsItem on Course {
    id
    title
    isArchived
    deletedBy {
      id
    }
    grades(filters: $courseGradeFilters) @include(if: $getGrades) {
      id
      name
      curriculumProgram @include(if: $getAssociatedProgram) {
        id
        acronym
      }
    }
    curriculumProgram @include(if: $getCurriculumProgram) {
      id
      type
      label
      acronym
    }
    learningCourse @include(if: $getLearningCourse) {
      id
      title
      academicCourse {
        id
        label
      }
    }
  }
`;

export const unreadAnnouncementsCountCommonFragment = gql`
  fragment unreadAnnouncementsCountItem on CircularConnectionV3 {
    unreadAnnouncementsCount: totalCount(input: $unreadAnnouncementsCountInput)
  }
`;

const basicUserFragment = gql`
  fragment userDetailItem on User {
    id
    firstName
    lastName
    middleName
    type
    profileImage
    email

    ... on Student {
      isArchived
      isDeleted
      preferredName
      prefix
      pronouns
      suffix
      yearGroup {
        id
        grade {
          id
          name
        }
      }
      sourceId
      alertTypes {
        ...alertTypeFields
      }
    }

    ... on Staff {
      isArchived
      isDeleted
      displayRole
      role
      preferredName
      prefix
      suffix
      pronouns
    }

    ... on FamilyMember {
      preferredName
      prefix
      pronouns
      suffix
      children(organizationId: $organizationId) {
        id
        firstName
        lastName
        middleName
        preferredName
        prefix
        pronouns
        suffix
        sourceId
      }
    }
    ... on Parent {
      preferredName
      prefix
      pronouns
      suffix
      children {
        id
        firstName
        lastName
        middleName
        preferredName
        prefix
        pronouns
        suffix
        sourceId
      }
    }
  }
  ${studentsAlertFragment}
`;

const basicUserFragmentV2 = gql`
  fragment userDetailItemV2 on User {
    id
    firstName
    lastName
    middleName
    type
    profileImage
    email

    ... on Student {
      isArchived
      isDeleted
      preferredName
      prefix
      pronouns
      suffix
      sourceId
      yearGroup {
        id
        grade {
          id
          name
        }
        curriculumProgram @include(if: $includeCurriculumPrograms) {
          id
          label
        }
      }
      alertTypes {
        ...alertTypeFields
      }
    }

    ... on Staff {
      isArchived
      isDeleted
      displayRole
      role
      preferredName
      prefix
      suffix
      pronouns
      curriculumPrograms @include(if: $includeCurriculumPrograms) {
        id
        label
      }
    }

    ... on FamilyMember {
      preferredName
      prefix
      pronouns
      suffix
      children(organizationId: $organizationId) {
        id
        firstName
        lastName
        middleName
        preferredName
        prefix
        pronouns
        suffix
        sourceId
        email
      }
    }
  }
  ${studentsAlertFragment}
`;

// common fragments for manage members
export const manageMembersFragments = {
  basicUserItem: basicUserFragment,
  basicUserItemV2: basicUserFragmentV2,
  userItem: gql`
    fragment userItem on User {
      ...userDetailItem

      ... on Student {
        sourceId
        isArchived
        isDeleted
        preferredName
        pronouns
        prefix
        suffix

        yearGroup {
          id
          grade {
            id
            name
          }
        }

        familyMembers @include(if: $getStudentFamilyMembers) {
          id
          firstName
          lastName
          middleName
          type
          profileImage
          preferredName
          pronouns
          suffix
          prefix

          children(organizationId: $organizationId) {
            id
            firstName
            lastName
            middleName
            preferredName
            pronouns
            suffix
            prefix
            sourceId
          }
        }
      }
    }
    ${basicUserFragment}
  `,
  userItemV2: gql`
    fragment userItemV2 on User {
      ...userDetailItemV2

      ... on Student {
        sourceId
        isArchived
        isDeleted
        preferredName
        pronouns
        prefix
        suffix

        yearGroup {
          id
          grade {
            id
            name
          }
          curriculumProgram @include(if: $includeCurriculumPrograms) {
            id
            label
          }
        }

        familyMembers @include(if: $getStudentFamilyMembers) {
          id
          firstName
          lastName
          middleName
          type
          profileImage
          preferredName
          pronouns
          suffix
          prefix

          children(organizationId: $organizationId) {
            id
            firstName
            lastName
            middleName
            preferredName
            pronouns
            suffix
            prefix
            sourceId
          }
        }
      }
    }
    ${basicUserFragmentV2}
  `,
};
